/**
 * Cloudflare Turnstile (Managed mode) — the challenge-response gate required
 * by the IRS e-file security standards for Online Providers. The widget runs
 * a background browser check and mints short-lived, single-use tokens; the
 * backend rejects gated auth requests that don't carry a valid token
 * (see pm_app/turnstile_gate.py in the backend repo).
 *
 * Usage (auth screens only):
 *   <TurnstileWidget />                       // once per page, above the CTA
 *   const token = await takeTurnstileToken(); // right before each gated POST
 *   post("/auth/sign-in/", { ...fields, turnstile_token: token });
 *
 * Most users see the widget verify itself with no interaction; suspicious
 * clients get a single checkbox tap, never a puzzle. Each submission takes
 * a fresh token, and pending submissions survive verification retries.
 *
 * Dev builds default to Cloudflare's official always-pass test sitekey so
 * localhost works without dashboard changes; the local backend pairs it with
 * the test secret key (see backend .env).
 */
import { useEffect, useRef, useSyncExternalStore } from "react";
import * as Sentry from "@sentry/react";
import { authPageOpened, authStep, watchAuthStep } from "../utils/authDiagnostics";

const TURNSTILE_SITE_KEY =
  import.meta.env.VITE_TURNSTILE_SITE_KEY ||
  (import.meta.env.DEV
    ? "1x00000000000000000000AA" // Cloudflare's official "always passes" test sitekey
    : "0x4AAAAAAEb6R34rnO-7BrDJ"); // production sitekey (public by design)

const SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

// Module-level singleton: auth screens render one widget at a time, and the
// token handshake (mint -> take -> reset) is shared between the component and
// the pages that consume tokens.
let scriptPromise = null;
let widgetId = null;
let currentToken = null;
let waiters = [];
let state = { phase: "checking", waiting: false };
const listeners = new Set();
const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
const getSnapshot = () => state;
const publish = (phase = state.phase) => {
  state = { phase, waiting: waiters.length > 0 };
  listeners.forEach((listener) => listener());
};
const report = (phase, code, notify = true) => {
  publish(phase);
  Sentry.addBreadcrumb({ category: "turnstile", message: phase, data: { code } });
  authStep(`verification_${phase}`, { code }, notify && ["error", "expired", "stalled"].includes(phase) ? "warning" : undefined);
};

// Auth pages can keep their form busy without covering an interactive check.
// eslint-disable-next-line react-refresh/only-export-components
export const useTurnstileState = () => useSyncExternalStore(subscribe, getSnapshot);

const loadScript = () => {
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = SCRIPT_SRC;
    script.async = true;
    const fail = (reason) => {
      clearTimeout(timer);
      script.onload = script.onerror = null;
      scriptPromise = null;
      script.remove();
      reject(new Error(reason));
    };
    authStep("verification_script_requested");
    const timer = setTimeout(() => fail("script_load_timeout"), 20000);
    script.onload = () => {
      clearTimeout(timer);
      authStep("verification_script_loaded");
      resolve();
    };
    script.onerror = () => fail("script_load_error");
    document.head.appendChild(script);
  });
  return scriptPromise;
};

const resetWidget = () => {
  currentToken = null;
  report("checking");
  try {
    if (widgetId !== null && window.turnstile) {
      window.turnstile.reset(widgetId);
    }
  } catch (error) {
    authStep("verification_reset_failed", { error: error.name }, "warning");
    // Widget already unmounted; the next mount renders fresh.
  }
};

// Called by Turnstile whenever the challenge completes. Hand the token to the
// oldest waiter if one is pending, otherwise store it for the next action.
const handleToken = (token) => {
  report("verified");
  if (waiters.length > 0) {
    const { resolve, cleanup } = waiters.shift();
    cleanup();
    authStep("verification_token_taken", { cached: false });
    resolve(token);
    publish();
    if (waiters.length) resetWidget();
  } else {
    currentToken = token;
  }
};

/**
 * Wait for verification, including user interaction and in-place retries.
 * Leaving the page cancels the wait; a later solve cannot submit the old form.
 */
// The token helper must share the widget's module state; auth pages import both.
// eslint-disable-next-line react-refresh/only-export-components
export const takeTurnstileToken = ({ signal } = {}) => {
  if (signal?.aborted) return Promise.reject(new DOMException("Cancelled", "AbortError"));
  if (currentToken) {
    const token = currentToken;
    currentToken = null;
    authStep("verification_token_taken", { cached: true });
    return Promise.resolve(token);
  }
  return new Promise((resolve, reject) => {
    const cancel = () => {
      waiters = waiters.filter((item) => item !== waiter);
      waiter.cleanup();
      reject(new DOMException("Cancelled", "AbortError"));
      publish();
      authStep("verification_wait_cancelled");
    };
    const waiter = { resolve, cancel, cleanup: () => signal?.removeEventListener("abort", cancel) };
    waiters.push(waiter);
    signal?.addEventListener("abort", cancel, { once: true });
    // A previously consumed token needs a fresh challenge for the next POST.
    if (state.phase === "verified") resetWidget();
    publish();
  });
};

// appearance: "always" shows the widget; "interaction-only" keeps it invisible
// unless Cloudflare needs the user to tap (auto-submitting code screens).
const TurnstileWidget = ({ className = "", appearance = "always" }) => {
  const containerRef = useRef(null);
  const retryRef = useRef(null);
  const { phase, waiting } = useTurnstileState();

  useEffect(() => {
    let cancelled = false;
    currentToken = null;
    authPageOpened();
    const errors = new Set();
    const fail = (code) => {
      report("error", code, !errors.has(code));
      errors.add(code);
    };
    const render = () => {
      report("checking");
      loadScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        containerRef.current.innerHTML = "";
        widgetId = window.turnstile.render(containerRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: handleToken,
          "expired-callback": () => {
            currentToken = null;
            report("expired", "token_expired");
          },
          "error-callback": (code) => {
            currentToken = null;
            fail(code);
          },
          "before-interactive-callback": () => report("interactive"),
          "after-interactive-callback": () => report("checking"),
          "timeout-callback": () => report("expired", "interaction_timeout"),
          "unsupported-callback": () => fail("unsupported_browser"),
          theme: "light",
          size: "flexible",
          appearance,
          "refresh-expired": "auto",
          "refresh-timeout": "manual",
        });
        authStep("verification_widget_rendered");
      })
      .catch((error) => {
        if (!cancelled) fail(error.message.startsWith("script_load") ? error.message : "widget_render_error");
      });
    };
    retryRef.current = () => {
      report("retry");
      if (widgetId !== null) resetWidget();
      else render();
    };
    render();
    return () => {
      cancelled = true;
      retryRef.current = null;
      [...waiters].forEach((waiter) => waiter.cancel());
      try {
        if (widgetId !== null && window.turnstile) {
          window.turnstile.remove(widgetId);
        }
      } catch {
        // Already gone.
      }
      widgetId = null;
      currentToken = null;
    };
  }, [appearance]);

  useEffect(() => {
    if (waiting) return watchAuthStep("verification_wait");
  }, [waiting]);

  useEffect(() => {
    if (!waiting || phase !== "checking") return;
    // Offer recovery for a hung script or challenge without abandoning login.
    const timer = setTimeout(() => report("stalled"), 20000);
    return () => clearTimeout(timer);
  }, [waiting, phase]);

  const needsRetry = ["error", "expired", "stalled"].includes(phase);
  return (
    <div className={className}>
      <div ref={containerRef} data-turnstile-widget />
      {(waiting || needsRetry) && (
        <div className="mt-3 text-sm text-[#717171]" role="status" aria-live="polite">
          {needsRetry ? "Verification hasn't completed. Please try again."
            : phase === "interactive" ? "Complete the security check to continue."
            : "Checking your browser…"}
          {needsRetry && (
            <button type="button" onClick={() => retryRef.current?.()}
              className="block mt-2 text-black underline underline-offset-4">
              Try verification again
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default TurnstileWidget;
